/**
 * Compete: the player's profile in this app. Points and level, the badge shelf,
 * friendly challenges, this week's ladder and their squad.
 *
 * Everything on this screen is free. Nothing here can be bought, points and
 * badges unlock nothing, and there is no price in this file by design.
 *
 * It also paints the small points strip on the dashboard (paintHero), which
 * the dashboard repaints often, so that path is cached and never throws.
 */

import { TUNABLES } from '../scoring.js';
import { icon as elementIcon } from './elements.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const $ = (id) => document.getElementById(id);
const arr = (v) => (Array.isArray(v) ? v : []);
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = (n) => num(n).toLocaleString('en-US');
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, num(v)));

// The Worker sends an element as its label ("Fire"), worker/points.js as its id
// ("fire"), so this reads either and both screens get the same tile.
const ELEMENTS = Array.isArray(TUNABLES?.ELEMENTS) ? TUNABLES.ELEMENTS : [];
const elementBy = (v) => {
  const k = String(v ?? '').trim().toLowerCase();
  return k ? ELEMENTS.find((e) => e.id === k || e.label.toLowerCase() === k) || null : null;
};

// The three things a challenge can be scored on. Same words the Worker uses.
const METRICS = [
  { id: 'element', label: 'Element', blurb: 'the element tier of each session' },
  { id: 'form', label: 'Form', blurb: 'your average overall form score' },
  { id: 'makes', label: 'Makes', blurb: 'makes out of attempts' },
];
const metricBy = (id) => METRICS.find((m) => m.id === id) || METRICS[0];

// Fixed reasons only, the same words the Worker files. There is no free text
// between players anywhere in this app, and a report is not one either. The
// first four are safety reports: a person reads them first, and a reported
// coach, scout or agent loses access to every player under 18 while it is
// reviewed (worker/vetting.js). A safety report may carry a few words for
// that person, and only for them.
const REASONS = [
  { id: 'safety', label: 'Something they did made me feel unsafe', safety: true },
  { id: 'private', label: 'They asked to talk in private or off Hooper Buddy', safety: true },
  { id: 'gifts', label: 'They offered gifts, money or rides', safety: true },
  { id: 'harassment', label: 'They are bothering me', safety: true },
  { id: 'handle', label: 'Their handle is not okay' },
  { id: 'cheating', label: 'Their scores look faked' },
  { id: 'spam', label: 'They keep spamming invites' },
];
// A child this viewer may not see comes back with no handle (worker/social.js rule 10).
const who = (r) => (r?.hidden ? 'A teammate' : (r?.handle || 'Player'));

let ctx = null;
let social = null;          // GET /api/social
let socialError = '';
let points = null;          // GET /api/points
let pointsError = '';
let pointsAt = 0;           // when the points cache was filled
let pointsPending = null;   // the in flight points request, so two never run
let heroEl = null;          // the dashboard strip, repainted when the cache moves
let celebrate = [];         // badges earned since the last paint, for the cheer
let cheerTimer = null;
let ladder = null;          // the ladder on screen
let ladderError = '';
let ladderScope = 'squad';
let openChallenge = null;   // the challenge whose board is open
const boards = new Map();   // challenge id -> GET /challenges/:id
let reporting = null;       // the account id whose report reasons are showing
let inviteCode = null;      // the squad share code, once asked for

/* ------------------------------------------------------------------ mount */

export function mount(c) {
  ctx = c;
  const root = $('socialRoot');
  if (!root) return;
  root.innerHTML = shell();
  bind();
}

function shell() {
  return `
  <div class="soc-wrap">
    <p class="soc-free">Free. Nothing to buy. No prizes. Points are just for fun.</p>

    <div class="card soc-me">
      <h3>Your card</h3>
      <div id="socMeBody" class="soc-loading">Loading your card...</div>
    </div>

    <div class="card soc-badges">
      <h3>Badges</h3>
      <div id="socCheer" class="soc-cheer" hidden></div>
      <div id="socBadges" class="soc-loading">Loading badges...</div>
    </div>

    <div class="card soc-chal">
      <h3>Challenges</h3>
      <p class="sub">Pick a target, share the code with people you know, and whoever trains best before
        the window closes takes it. Your score is read from your own training sessions, so there is
        nothing to type in and nothing to fake.</p>
      <div class="soc-forms">
        <div class="soc-form">
          <b>Start one</b>
          <label>Name<input id="socChalTitle" type="text" maxlength="60" placeholder="Friday form battle"></label>
          <label>Scored on<select id="socChalMetric">${METRICS.map((m) => `<option value="${m.id}">${m.label}, ${m.blurb}</option>`).join('')}</select></label>
          <div class="soc-two">
            <label>Shots each<input id="socChalShots" type="number" min="5" max="500" step="5" value="25"></label>
            <label>Days<input id="socChalDays" type="number" min="1" max="30" value="7"></label>
          </div>
          <label>Who can join<select id="socChalScope"></select></label>
          <button class="btn primary wide" id="socChalCreate">Create challenge</button>
          <p class="sub" id="socChalNote"></p>
        </div>
        <div class="soc-form">
          <b>Join one</b>
          <label>Join code<input id="socJoinCode" type="text" maxlength="12" placeholder="H7KMX3" autocapitalize="characters" autocomplete="off"></label>
          <button class="btn wide" id="socJoinGo">Join with this code</button>
          <p class="sub">Someone shares their code, you paste it here. Joining costs nothing.</p>
        </div>
      </div>
      <div id="socChalList" class="soc-loading">Loading your challenges...</div>
    </div>

    <div class="card soc-ladder">
      <h3>This week</h3>
      <div class="soc-scopes" id="socScopes"></div>
      <div id="socLadder" class="soc-loading">Loading the ladder...</div>
    </div>

    <div class="card soc-squad">
      <h3>Your squad</h3>
      <p class="sub">The people you train against. You see their handle, their element of the day and
        their streak, and that is all. There are no messages in Hooper Buddy, anywhere.</p>
      <div class="soc-forms">
        <div class="soc-form">
          <b>Invite someone</b>
          <label>Their email<input id="socInviteEmail" type="email" maxlength="120" placeholder="teammate@example.com" autocomplete="off"></label>
          <div class="soc-row">
            <button class="btn primary" id="socInviteSend">Send invite</button>
            <button class="btn" id="socInviteCode">Get a share code</button>
          </div>
          <p class="sub" id="socInviteOut"></p>
        </div>
        <div class="soc-form">
          <b>Accept a code</b>
          <label>Squad code<input id="socAcceptCode" type="text" maxlength="12" placeholder="H7KMX3" autocapitalize="characters" autocomplete="off"></label>
          <button class="btn wide" id="socAcceptGo">Join their squad</button>
          <p class="sub">A code works for a few days, and anyone it is given to can use it. Nobody can add you without it.</p>
        </div>
      </div>
      <div id="socInvites"></div>
      <div id="socSquad" class="soc-loading">Loading your squad...</div>
    </div>
  </div>`;
}

function bind() {
  const root = $('socialRoot');
  if (!root) return;
  $('socChalCreate').onclick = createChallenge;
  $('socJoinGo').onclick = joinChallenge;
  $('socInviteSend').onclick = () => inviteSquad(true);
  $('socInviteCode').onclick = () => inviteSquad(false);
  $('socAcceptGo').onclick = acceptSquad;

  // One listener for everything the sections repaint.
  root.addEventListener('click', (e) => {
    const t = (name) => e.target.closest(`[data-${name}]`);
    let el;
    if ((el = t('handle-set'))) return setHandle($('socHandle')?.value);
    if ((el = t('handle-change'))) return changeHandle();
    if ((el = t('pref'))) return togglePref(el.dataset.pref);
    if ((el = t('scope'))) return switchScope(el.dataset.scope);
    if ((el = t('chal-open'))) return openBoard(el.dataset.chalOpen);
    if ((el = t('chal-copy'))) return copy(el.dataset.chalCopy, 'Join code copied.');
    if ((el = t('chal-leave'))) return leaveChallenge(el.dataset.chalLeave);
    if ((el = t('code-copy'))) return copy(el.dataset.codeCopy, 'Share code copied.');
    if ((el = t('friend-remove'))) return removeFriend(el.dataset.friendRemove, el.dataset.handle);
    if ((el = t('friend-block'))) return blockAccount(el.dataset.friendBlock, el.dataset.handle);
    if ((el = t('friend-report'))) { reporting = reporting === el.dataset.friendReport ? null : el.dataset.friendReport; return paintSquad(); }
    if ((el = t('reason'))) return reportAccount(el.dataset.account, el.dataset.reason, el.dataset.handle);
  });
}

/* ------------------------------------------------------------------ fetch */

async function loadSocial() {
  try {
    social = await ctx.api('/api/social');
    socialError = '';
  } catch (e) { social = null; socialError = e.message; }
}

/**
 * Points, badges and the level. Shared with the dashboard strip: one cache,
 * one request in flight at a time. justEarned only comes back the first time
 * a badge is seen, so it is parked in celebrate() until the Compete screen
 * paints, wherever the fetch was fired from.
 */
async function loadPoints(force = false) {
  if (!force && points && Date.now() - pointsAt < 60000) return points;
  if (pointsPending) return pointsPending;
  pointsPending = ctx.api('/api/points')
    .then((r) => {
      points = r || null; pointsError = ''; pointsAt = Date.now();
      const fresh = arr(r?.justEarned);
      if (fresh.length) celebrate = celebrate.concat(fresh).slice(-6);
      drawHero();
      return points;
    })
    .catch((e) => { pointsError = e.message; pointsAt = Date.now(); return null; })
    .finally(() => { pointsPending = null; });
  return pointsPending;
}

async function loadLadder(scope) {
  ladderScope = scope;
  ladderError = '';
  if (scope === 'global' && (isMinor() || !prefs().ladderOptIn)) { ladder = null; return; }
  // The first paint can use the ladder the summary already sent.
  const given = asLadder(social?.ladder);
  if (given && (given.scope === scope || !given.scope)) { ladder = { ...given, scope }; return; }
  try {
    ladder = asLadder(await ctx.api(`/api/social/ladder?scope=${encodeURIComponent(scope)}`));
  } catch (e) { ladder = null; ladderError = e.message; }
}

const asLadder = (v) => {
  if (!v) return null;
  if (Array.isArray(v)) return { scope: null, week: null, rows: v, optIn: null, note: '' };
  return { scope: v.scope || null, week: v.week || null, rows: arr(v.rows),
    optIn: typeof v.optIn === 'boolean' ? v.optIn : null, note: v.note || '' };
};

/* ----------------------------------------------------------------- render */

export async function render() {
  if (!ctx || !$('socMeBody')) return;
  // The summary carries a fresh board for every challenge, so an older cached
  // one would shadow it and show scores that have since moved.
  boards.clear();
  await Promise.all([loadSocial(), loadPoints(true)]);
  const scopes = ladderScopes();
  if (!scopes.some((s) => s.id === ladderScope)) ladderScope = scopes[0]?.id || 'squad';
  await loadLadder(ladderScope);
  paintAll();
}

function paintAll() {
  paintMe();
  paintBadges();
  paintChallenges();
  paintScopes();
  paintLadder();
  paintSquad();
  paintChallengeScopes();
}

/* --------------------------------------------------------------- the card */

const me = () => (social && social.me && typeof social.me === 'object' ? social.me : {});

function prefs() {
  const m = me();
  const s = m.social && typeof m.social === 'object' ? m.social : m;
  return { ladderOptIn: Boolean(s.ladderOptIn), discoverable: Boolean(s.discoverable) };
}

/** The server decides for real. This only picks which words the screen shows. */
function isMinor() {
  const m = me();
  if (typeof m.adult === 'boolean') return !m.adult;
  const band = m.ageBand || ctx?.S?.settings?.ageBand;
  return band ? band !== 'adult' : false;
}

const orgList = () => arr(me().orgs).filter((o) => o && o.id);

function myElement() {
  const m = me();
  const id = m.element || points?.topElement;
  if (id) return elementBy(id) || null;
  const mine = arr(ladder?.rows).find((r) => r && r.you);
  return mine?.element ? elementBy(mine.element) : null;
}

function paintMe() {
  const box = $('socMeBody');
  if (!box) return;
  const m = me();
  const p = points || {};
  const streak = p.streak || m.streak || {};
  const el = myElement();
  const pct = clamp(p.progressPct, 0, 100);
  const badges = arr(p.badges).length;

  if (socialError && !m.handle) {
    box.innerHTML = `<p class="soc-err">${esc(socialError)}</p>` + pointsBlock(p, pct, streak, el, badges);
    return;
  }

  const head = m.handle ? `
    <div class="soc-id">
      <span class="soc-av">${esc(initials(m.handle))}</span>
      <div class="soc-idtext">
        <b class="soc-handle">${esc(m.handle)}</b>
        <span class="pill hot">${esc(p.levelName || 'Level one')}</span>
      </div>
      <button class="btn soc-mini" data-handle-change>Change handle</button>
    </div>` : `
    <div class="soc-handleset">
      <label>Pick a player name<input id="socHandle" type="text" maxlength="20" placeholder="crossover_kid" autocomplete="off"></label>
      <button class="btn primary" data-handle-set>Save name</button>
      <p class="sub">3 to 20 letters or numbers. Other players see this name. <b>Do not use your real name, email, phone or school.</b></p>
    </div>`;

  // Rule 9 on the server: no age set, or under 13 with no parent confirmed,
  // and nobody else sees or reaches this player. Say so plainly.
  const closed = m.closed ? `<p class="soc-note"><b>Nobody else can see you or reach you here yet.</b> ${esc(m.closed)}</p>` : '';
  box.innerHTML = closed + head + pointsBlock(p, pct, streak, el, badges);
}

function pointsBlock(p, pct, streak, el, badges) {
  if (pointsError) return `<p class="soc-err">Could not load your points: ${esc(pointsError)}</p>`;
  // The top level has no next level to point at, so nextLevelAt is what says so.
  const toNext = p.nextLevelAt == null ? null : num(p.toNext);
  return `
    <div class="soc-score">
      <div class="soc-big"><b>${fmt(p.points)}</b><small>points</small></div>
      <div class="soc-prog">
        <div class="soc-progtop"><span>${esc(p.levelName || 'Level one')}</span><span>${pct}%</span></div>
        <div class="bar"><i style="width:${pct}%"></i></div>
        <p class="sub">${toNext === null ? 'Top level. Nothing left to climb.' : `${fmt(toNext)} points to ${esc(p.nextLevelName || 'your next level')}.`}</p>
      </div>
    </div>
    <div class="grid-stats soc-stats">
      <div class="stat"><b>${fmt(streak.days)}</b><small>day streak</small></div>
      <div class="stat"><b>${fmt(streak.best)}</b><small>best streak</small></div>
      <div class="stat"><b>${el ? esc(el.label) : 'None yet'}</b><small>your element</small></div>
      <div class="stat"><b>${fmt(badges)}</b><small>badges</small></div>
    </div>
    ${arr(p.breakdown).length ? `
      <details class="soc-break">
        <summary>Where your points come from</summary>
        ${arr(p.breakdown).map((b) => `
          <div class="soc-brk">
            <span class="soc-brk-l"><b>${esc(b.label)}</b>${b.detail ? `<small>${esc(b.detail)}</small>` : ''}</span>
            <span class="soc-brk-p">${fmt(b.points)}</span>
          </div>`).join('')}
      </details>` : ''}`;
}

const initials = (handle) => String(handle || '').trim().replace(/[^A-Za-z0-9 _]/g, '').split(/[ _]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

async function setHandle(value) {
  const handle = String(value || '').trim();
  if (handle.length < 3) return ctx.toast('A handle needs at least three characters.', true);
  try {
    const r = await ctx.api('/api/social/handle', { method: 'POST', body: JSON.stringify({ handle }) });
    ctx.toast(`You are ${r?.handle || handle} on the boards now.`);
    await render();
  } catch (e) { ctx.toast(e.message, true); }
}

async function changeHandle() {
  const next = await ctx.ui.prompt({
    title: 'Change your handle',
    body: '3 to 20 letters or numbers. Other players see this name.',
    label: 'Handle', value: me().handle || '', confirmText: 'Save',
  });
  if (next == null) return;
  await setHandle(next);
}

/* ------------------------------------------------------------------ badges */

/** Icons come from worker/points.js, never from a player, so a real inline svg
 *  is rendered as it is. Anything else is treated as text and escaped. */
function badgeIcon(b) {
  const raw = String(b?.icon || '').trim();
  if (/^<svg[\s>]/i.test(raw)) return raw;
  return `<span class="soc-glyph">${esc(raw || '\u2605')}</span>`;
}

function paintBadges() {
  const box = $('socBadges');
  if (!box) return;
  if (pointsError) { box.className = ''; box.innerHTML = `<p class="soc-err">${esc(pointsError)}</p>`; return; }
  const earned = arr(points?.badges);
  const locked = arr(points?.locked);
  const fresh = new Set(celebrate.map((b) => b.id));
  box.className = 'soc-badgebox';
  if (!earned.length && !locked.length) {
    box.innerHTML = '<p class="soc-empty">Badges show up here as you use the app. Put a session in and the first ones are quick.</p>';
    paintCheer();
    return;
  }
  box.innerHTML = `
    <p class="sub soc-badgecount">${earned.length} of ${num(points?.badgeTotal) || earned.length + locked.length} earned. The locked ones say what gets them. They unlock nothing, they are just yours.</p>
    <div class="soc-shelf">
      ${earned.map((b) => tile(b, false, fresh.has(b.id))).join('')}
      ${locked.map((b) => tile(b, true, false)).join('')}
    </div>`;
  paintCheer();
}

function tile(b, locked, fresh) {
  const when = b.earnedAt && Number.isFinite(Date.parse(b.earnedAt))
    ? new Date(b.earnedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
  return `
    <div class="soc-badge${locked ? ' locked' : ''}${fresh ? ' fresh' : ''}${b.tier ? ` t-${esc(b.tier)}` : ''}" title="${esc(b.blurb || b.name || '')}">
      <span class="soc-bicon">${badgeIcon(b)}</span>
      <b>${esc(b.name || 'Badge')}</b>
      <small>${esc(b.blurb || '')}</small>
      ${locked ? '<span class="soc-lockword">Not yet</span>' : (when ? `<span class="soc-when">${esc(when)}</span>` : '')}
    </div>`;
}

/** A moment, not a modal: a strip that says what landed and fades itself out. */
function paintCheer() {
  const box = $('socCheer');
  if (!box) return;
  if (!celebrate.length) { box.hidden = true; box.className = 'soc-cheer'; box.innerHTML = ''; return; }
  box.hidden = false;
  box.className = 'soc-cheer on';
  box.innerHTML = `
    <span class="soc-cheer-icons">${celebrate.map(badgeIcon).join('')}</span>
    <span class="soc-cheer-text">
      <b>${celebrate.length === 1 ? 'New badge' : `${celebrate.length} new badges`}</b>
      <small>${esc(celebrate.map((b) => b.name).filter(Boolean).join(', '))}</small>
    </span>`;
  clearTimeout(cheerTimer);
  cheerTimer = setTimeout(() => { celebrate = []; const b = $('socCheer'); if (b) { b.hidden = true; b.className = 'soc-cheer'; b.innerHTML = ''; } }, 9000);
}

/* -------------------------------------------------------------- challenges */

/**
 * Rule 2 in the words the player reads: a minor competes only inside a family
 * or team they are already in, so their list is their groups and nothing else.
 * An open challenge is still free, it is just for adults.
 */
function challengeScopes() {
  // The Worker keeps two challenge scopes and only two: one of your groups, or
  // open to whoever has the code. There is no squad scope for it to enforce, so
  // this does not offer one and then quietly create something wider.
  const orgs = ladderScopes().filter((s) => String(s.id).startsWith('org:'));
  return isMinor() ? orgs : [...orgs, { id: 'open', label: 'Anyone with the code' }];
}

function paintChallengeScopes() {
  const sel = $('socChalScope');
  const note = $('socChalNote');
  const create = $('socChalCreate');
  if (!sel) return;
  const keep = sel.value;
  const list = challengeScopes();
  if (!list.length) {
    // A minor with no family or team yet. Say why, plainly, and leave it there.
    sel.innerHTML = '<option value="">No group yet</option>';
    sel.disabled = true;
    if (create) create.disabled = true;
    if (note) note.textContent = 'You play with your family or your team. Never with strangers. Ask a parent or a coach to add you. Got a code? You can still join with it.';
    return;
  }
  sel.disabled = false;
  if (create) create.disabled = false;
  if (note) note.textContent = '';
  sel.innerHTML = list.map((s) => `<option value="${esc(s.id)}">${esc(s.label)}</option>`).join('');
  if (list.some((s) => s.id === keep)) sel.value = keep;
}

const daysLeft = (iso) => {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.ceil((t - Date.now()) / 864e5) : null;
};
const isClosed = (c) => c?.status === 'closed' || (daysLeft(c?.closesAt) !== null && daysLeft(c.closesAt) <= 0);

function paintChallenges() {
  const box = $('socChalList');
  if (!box) return;
  box.className = 'soc-list';
  const list = arr(social?.challenges);
  if (socialError) { box.innerHTML = `<p class="soc-err">${esc(socialError)}</p>`; return; }
  if (!list.length) {
    box.innerHTML = '<p class="soc-empty">No challenges yet. Start one and send the code to a friend, a teammate or your family. It costs nothing.</p>';
    return;
  }
  box.innerHTML = list.map(challengeCard).join('');
}

function challengeCard(c) {
  const left = Number.isFinite(Number(c.daysLeft)) ? num(c.daysLeft) : daysLeft(c.closesAt);
  const closed = isClosed(c);
  const open = openChallenge === c.id;
  const detail = boards.get(c.id) || c;
  const rows = arr(detail.board || detail.entries);
  const sorted = [...rows].sort((a, b) => num(b.score) - num(a.score));
  const mine = me().handle;
  const winner = closed ? (c.winner?.handle || sorted[0]?.handle || null) : null;
  return `
    <div class="soc-item${closed ? ' done' : ''}">
      <div class="soc-itop">
        <div class="soc-ihead">
          <b>${esc(c.title || 'Challenge')}</b>
          <div class="soc-itags">
            <span class="pill cool">${esc(metricBy(c.metric).label)}</span>
            <span class="pill">${fmt(c.shots)} shots</span>
            ${closed
              ? `<span class="pill good">${winner ? `Won by ${esc(winner)}` : 'Finished'}</span>`
              : `<span class="pill hot">${left === null ? 'Open' : left === 1 ? '1 day left' : `${fmt(left)} days left`}</span>`}
            <span class="pill">${arr(c.members).length || rows.length || 1} in</span>
          </div>
        </div>
        <div class="soc-iops">
          ${c.code ? `<button class="btn soc-mini" data-chal-copy="${esc(c.code)}">Copy code ${esc(c.code)}</button>` : ''}
          <button class="btn soc-mini" data-chal-open="${esc(c.id)}">${open ? 'Hide board' : 'Board'}</button>
          <button class="btn soc-mini" data-chal-leave="${esc(c.id)}">Leave</button>
        </div>
      </div>
      ${open ? `
        <div class="soc-board">
          ${sorted.length ? sorted.map((r, i) => {
            const el = elementBy(r.element);
            const you = r.you === true || (mine && !r.hidden && r.handle === mine);
            return `<div class="soc-brow${you ? ' you' : ''}">
              <span class="soc-rank">${i + 1}</span>
              <span class="soc-who">${esc(who(r))}${you ? '<i>you</i>' : ''}</span>
              <span class="soc-el">${el ? `${elementIcon(el.id, 14)}<span>${esc(el.label)}</span>` : ''}</span>
              <span class="soc-sc">${fmt(Math.round(num(r.score)))}</span>
            </div>`;
          }).join('') : '<p class="soc-empty">Nobody has posted a session into this one yet. Train and yours lands here on its own.</p>'}
        </div>` : ''}
    </div>`;
}

async function createChallenge() {
  const title = $('socChalTitle').value.trim();
  if (title.length < 2) return ctx.toast('Give the challenge a name first.', true);
  const btn = $('socChalCreate');
  btn.disabled = true;
  try {
    const c = await ctx.api('/api/social/challenges', {
      method: 'POST',
      body: JSON.stringify({
        title,
        metric: $('socChalMetric').value,
        shots: clamp($('socChalShots').value, 5, 500),
        days: clamp($('socChalDays').value, 1, 30),
        scope: $('socChalScope').value,
      }),
    });
    $('socChalTitle').value = '';
    if (c && c.id) openChallenge = c.id;
    ctx.toast(c && c.code ? `Challenge created. Share code ${c.code}` : 'Challenge created.');
    await render();
  } catch (e) { ctx.toast(e.message, true); } finally { btn.disabled = false; }
}

async function joinChallenge() {
  const code = $('socJoinCode').value.trim();
  if (code.length < 4) return ctx.toast('Paste the join code first.', true);
  const btn = $('socJoinGo');
  btn.disabled = true;
  try {
    const c = await ctx.api('/api/social/challenges/join', { method: 'POST', body: JSON.stringify({ code }) });
    $('socJoinCode').value = '';
    if (c && c.id) openChallenge = c.id;
    ctx.toast(c && c.title ? `You are in: ${c.title}` : 'You are in.');
    await render();
  } catch (e) { ctx.toast(e.message, true); } finally { btn.disabled = false; }
}

async function openBoard(id) {
  if (openChallenge === id) { openChallenge = null; paintChallenges(); return; }
  openChallenge = id;
  paintChallenges();
  if (boards.has(id)) return;
  try {
    const c = await ctx.api(`/api/social/challenges/${encodeURIComponent(id)}`);
    if (c) boards.set(id, c);
  } catch (e) { ctx.toast(e.message, true); }
  paintChallenges();
}

async function leaveChallenge(id) {
  const c = arr(social?.challenges).find((x) => x.id === id);
  const ok = await ctx.ui.confirm({
    title: 'Leave this challenge?',
    body: `${c?.title ? `${c.title} carries on without you.` : 'It carries on without you.'} Your sessions stop counting toward it. You can join again with the code.`,
    confirmText: 'Leave', danger: true,
  });
  if (!ok) return;
  try {
    await ctx.api(`/api/social/challenges/${encodeURIComponent(id)}/leave`, { method: 'POST' });
    boards.delete(id);
    if (openChallenge === id) openChallenge = null;
    ctx.toast('You left the challenge.');
    await render();
  } catch (e) { ctx.toast(e.message, true); }
}

/* ------------------------------------------------------------------ ladder */

/** The scopes the Worker says this player has, with a sane list if it is quiet. */
function ladderScopes() {
  const given = arr(social?.scopes).filter((s) => s && s.id);
  if (given.length) return given.map((s) => ({ id: String(s.id), label: s.label || String(s.id), adultsOnly: Boolean(s.adultsOnly) }));
  return [
    { id: 'squad', label: 'Squad' },
    ...orgList().map((o) => ({ id: `org:${o.id}`, label: o.name || 'Team' })),
    { id: 'global', label: 'Global', adultsOnly: true },
  ];
}

function paintScopes() {
  const box = $('socScopes');
  if (!box) return;
  box.innerHTML = ladderScopes().map((s) =>
    `<button class="btn soc-mini${s.id === ladderScope ? ' on' : ''}" data-scope="${esc(s.id)}">${esc(s.label)}</button>`).join('');
}

async function switchScope(scope) {
  const box = $('socLadder');
  if (box) { box.className = 'soc-loading'; box.textContent = 'Loading the ladder...'; }
  // A fresh scope always asks the server rather than reusing the summary's rows.
  if (social) social = { ...social, ladder: null };
  await loadLadder(scope);
  paintScopes();
  paintLadder();
}

function paintLadder() {
  const box = $('socLadder');
  if (!box) return;
  box.className = 'soc-ladderbox';

  if (ladderScope === 'global' && isMinor()) {
    box.innerHTML = `
      <p class="soc-note"><b>The global ladder is for players eighteen and up.</b>
        Under eighteen you compete with your own squad and the teams and family you are already in,
        never with strangers. Everything else on this screen works the same for you.</p>`;
    return;
  }
  if (ladderScope === 'global' && (!prefs().ladderOptIn || ladder?.optIn === false)) {
    box.innerHTML = `
      <p class="soc-note">The global ladder is off until you turn it on. Turning it on puts your handle,
        your score and your element on a public weekly board with other adults. Nothing else is shared,
        and you can turn it off whenever you want.</p>
      <div class="srow">
        <span class="lab"><b>Show me on the global ladder</b><small>Handle and score only, this week at a time.</small></span>
        <button class="sw" data-pref="ladderOptIn" aria-label="Show me on the global ladder"></button>
      </div>`;
    return;
  }
  if (ladderError) { box.innerHTML = `<p class="soc-err">${esc(ladderError)}</p>`; return; }

  const rows = arr(ladder?.rows);
  const optRow = ladderScope === 'global' ? `
      <div class="srow">
        <span class="lab"><b>Show me on the global ladder</b><small>On. Turn it off and your row comes down.</small></span>
        <button class="sw on" data-pref="ladderOptIn" aria-label="Show me on the global ladder"></button>
      </div>` : '';

  if (!rows.length) {
    box.innerHTML = `<p class="soc-empty">Nothing on this board yet this week. Finish a session and your row lands here on its own.</p>${optRow}`;
    return;
  }
  box.innerHTML = `
    ${ladder?.week ? `<p class="sub soc-week">Week ${esc(ladder.week)}. It resets every week, so a bad week is never permanent.</p>` : ''}
    <div class="soc-lt">
      ${rows.map((r, i) => {
        const el = elementBy(r.element);
        return `<div class="soc-brow${r.you ? ' you' : ''}">
          <span class="soc-rank">${fmt(r.rank || i + 1)}</span>
          <span class="soc-who">${esc(who(r))}${r.you ? '<i>you</i>' : ''}</span>
          <span class="soc-el">${el ? `${elementIcon(el.id, 14)}<span>${esc(el.label)}</span>` : ''}</span>
          <span class="soc-sc">${fmt(Math.round(num(r.score)))}</span>
        </div>`;
      }).join('')}
    </div>${optRow}`;
}

async function togglePref(key) {
  const current = prefs();
  const next = { [key]: !current[key] };
  try {
    const r = await ctx.api('/api/social/prefs', { method: 'PATCH', body: JSON.stringify(next) });
    const s = r && r.social && typeof r.social === 'object' ? r.social : next;
    if (social) social = { ...social, me: { ...me(), ...s, social: { ...(me().social || {}), ...s } }, ladder: null };
    await loadLadder(ladderScope);
    paintLadder();
    paintSquad();
  } catch (e) { ctx.toast(e.message, true); }
}

/* ------------------------------------------------------------------- squad */

function paintSquad() {
  const box = $('socSquad');
  if (!box) return;
  box.className = 'soc-list';
  paintInvites();
  const disc = prefs().discoverable;
  const discRow = `
    <div class="srow">
      <span class="lab"><b>Let people find me by my handle</b><small>Off means only a code or an email invite from you adds anyone.</small></span>
      <button class="sw${disc ? ' on' : ''}" data-pref="discoverable" aria-label="Let people find me by my handle"></button>
    </div>`;
  if (socialError) { box.innerHTML = `<p class="soc-err">${esc(socialError)}</p>`; return; }
  const squad = arr(social?.squad);
  if (!squad.length) {
    box.innerHTML = `<p class="soc-empty">Your squad is empty. Send one invite and the ladder gets a lot more interesting.</p>${discRow}`;
    return;
  }
  box.innerHTML = squad.map(friendRow).join('') + discRow;
}

function friendRow(f) {
  const el = elementBy(f.element);
  const id = String(f.accountId || '');
  const handle = String(f.handle || 'Player');
  const streak = num(f.streak?.days ?? f.streak);
  return `
    <div class="soc-friend">
      <span class="soc-av sm">${esc(initials(handle))}</span>
      <span class="soc-fwho">
        <b>${esc(handle)}</b>
        <small>${streak ? `${fmt(streak)} day streak` : 'No streak going'}${el ? ` &middot; ${esc(el.label)} today` : ''}</small>
      </span>
      <span class="soc-fel">${el ? elementIcon(el.id, 18) : ''}</span>
      <span class="soc-fops">
        <button class="btn soc-mini" data-friend-remove="${esc(id)}" data-handle="${esc(handle)}">Remove</button>
        <button class="btn soc-mini" data-friend-block="${esc(id)}" data-handle="${esc(handle)}">Block</button>
        <button class="btn soc-mini" data-friend-report="${esc(id)}">Report</button>
      </span>
      ${reporting === id ? `
        <span class="soc-reasons">
          <small>Tell us what is wrong. A person reads these.</small>
          ${REASONS.map((r) => `<button class="btn soc-mini" data-reason="${esc(r.id)}" data-account="${esc(id)}" data-handle="${esc(handle)}">${esc(r.label)}</button>`).join('')}
        </span>` : ''}
    </div>`;
}

function paintInvites() {
  const box = $('socInvites');
  if (!box) return;
  const list = arr(social?.invites);
  if (!list.length) { box.innerHTML = ''; return; }
  box.innerHTML = `<div class="soc-invites"><b>Waiting on</b>${list.map((i) => `
    <div class="soc-invite">
      <span>${esc(i.handle || i.email || i.title || 'Invite')}</span>
      ${i.code ? `<button class="btn soc-mini" data-code-copy="${esc(i.code)}">Copy code ${esc(i.code)}</button>` : ''}
    </div>`).join('')}</div>`;
}

async function inviteSquad(byEmail) {
  const email = $('socInviteEmail').value.trim();
  if (byEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return ctx.toast('Enter a real email, or ask for a share code instead.', true);
  const btn = byEmail ? $('socInviteSend') : $('socInviteCode');
  btn.disabled = true;
  try {
    const r = await ctx.api('/api/social/squad/invite', { method: 'POST', body: JSON.stringify(byEmail ? { email } : {}) });
    inviteCode = r?.code || null;
    const out = $('socInviteOut');
    if (byEmail) {
      $('socInviteEmail').value = '';
      out.textContent = `${r?.emailed ? `Invite sent to ${email}.` : `We could not email ${email} just now.`}${inviteCode ? ` Their code is ${inviteCode}, and it works for a few days.` : ''}`;
      ctx.toast(r?.emailed ? 'Invite sent.' : 'Invite made. Send them the code.');
    } else if (inviteCode) {
      out.innerHTML = `Your share code is <b>${esc(inviteCode)}</b>. Anyone you give it to can join your squad. <button class="btn soc-mini" data-code-copy="${esc(inviteCode)}">Copy</button>`;
    } else {
      out.textContent = 'Invite created.';
    }
    await render();
  } catch (e) { ctx.toast(e.message, true); } finally { btn.disabled = false; }
}

async function acceptSquad() {
  const code = $('socAcceptCode').value.trim();
  if (code.length < 4) return ctx.toast('Paste their squad code first.', true);
  const btn = $('socAcceptGo');
  btn.disabled = true;
  try {
    const r = await ctx.api('/api/social/squad/accept', { method: 'POST', body: JSON.stringify({ code }) });
    $('socAcceptCode').value = '';
    ctx.toast(r?.friend?.handle ? `${r.friend.handle} is in your squad.` : 'You are in their squad.');
    await render();
  } catch (e) { ctx.toast(e.message, true); } finally { btn.disabled = false; }
}

async function removeFriend(accountId, handle) {
  const ok = await ctx.ui.confirm({
    title: 'Remove from your squad?',
    body: `${handle || 'They'} comes off your squad and out of your squad ladder. You can add them back with a code.`,
    confirmText: 'Remove', danger: true,
  });
  if (!ok) return;
  try {
    await ctx.api(`/api/social/squad/${encodeURIComponent(accountId)}`, { method: 'DELETE' });
    ctx.toast('Removed from your squad.');
    await render();
  } catch (e) { ctx.toast(e.message, true); }
}

async function blockAccount(accountId, handle) {
  const ok = await ctx.ui.confirm({
    title: 'Block this player?',
    body: `${handle || 'This player'} disappears from every board, challenge and squad you are in, and you disappear from theirs. It works both ways and it is permanent, so only block someone you never want to see again.`,
    confirmText: 'Block', danger: true,
  });
  if (!ok) return;
  try {
    await ctx.api('/api/social/block', { method: 'POST', body: JSON.stringify({ accountId }) });
    reporting = null;
    ctx.toast('Blocked. You will not see each other again.');
    await render();
  } catch (e) { ctx.toast(e.message, true); }
}

async function reportAccount(accountId, reason, handle) {
  const picked = REASONS.find((r) => r.id === reason);
  const label = picked?.label || 'a problem';
  let details = '';
  if (picked?.safety) {
    // Only for the person who reads it, never for the player reported.
    const said = await ctx.ui.prompt({
      title: 'Tell us what happened',
      body: `${label}${handle ? `, about ${handle}` : ''}. A person reads this. If you are in danger right now, tell a parent or call 911. Anything you write here goes only to Hooper Buddy.`,
      label: 'What happened (optional)', placeholder: '', confirmText: 'Send report',
    });
    if (said == null) return;
    details = String(said);
  } else {
    const ok = await ctx.ui.confirm({
      title: 'Send this report?',
      body: `${label}${handle ? `, about ${handle}` : ''}. A person at Hooper Buddy reads it, and nobody else sees it. If you want them gone right now, block them too.`,
      confirmText: 'Send report',
    });
    if (!ok) return;
  }
  try {
    await ctx.api('/api/social/report', { method: 'POST', body: JSON.stringify({ accountId, reason, details }) });
    reporting = null;
    ctx.toast('Report sent. Thank you for telling us.');
    paintSquad();
  } catch (e) { ctx.toast(e.message, true); }
}

/* ------------------------------------------------------------------ shared */

async function copy(text, said) {
  try { await navigator.clipboard.writeText(String(text)); ctx.toast(said); }
  catch { ctx.toast(`Here it is: ${text}`); }
}

/* --------------------------------------------------------- dashboard strip
 * renderDashboard() calls this on every paint, so it never fetches twice in a
 * row, never throws, and draws nothing at all until there is something to show.
 */

/**
 * Badges that arrived with a finished session report. The server has already
 * recorded them, so this only makes the screens tell the truth again: drop the
 * cached summary and repaint whatever is on screen.
 */
export function noteEarned(earned) {
  try {
    if (!Array.isArray(earned) || !earned.length) return;
    pointsAt = 0;                       // force the next paint to refetch
    if (heroEl && ctx) loadPoints(true);
    if (ctx?.S?.view === 'social') render();
  } catch (e) { console.warn('badge refresh failed', e.message); }
}

export function paintHero(el) {
  try {
    if (el) heroEl = el;
    if (!heroEl || !ctx) return;
    // A failed request backs off for a minute instead of retrying on every paint.
    if (!points && !pointsPending) { if (Date.now() - pointsAt > 60000) loadPoints(); return; }
    if (points && Date.now() - pointsAt > 60000) loadPoints(true);
    drawHero();
  } catch (e) { console.warn('points strip failed', e.message); }
}

function drawHero() {
  try {
    if (!heroEl) return;
    const p = points;
    // Nothing earned yet is not an empty card, it is no card at all.
    if (!p || (!num(p.points) && !arr(p.badges).length)) { heroEl.innerHTML = ''; return; }
    const pct = clamp(p.progressPct, 0, 100);
    const badges = arr(p.badges).slice(0, 5);   // the summary sends them newest first
    const streak = p.streak || {};
    heroEl.innerHTML = `
      <div class="card soc-hero">
        <h3>Your points</h3>
        <div class="soc-herorow">
          <div class="soc-big"><b>${fmt(p.points)}</b><small>points</small></div>
          <div class="soc-prog">
            <div class="soc-progtop"><span>${esc(p.levelName || 'Level one')}</span><span>${num(streak.days) ? `${fmt(streak.days)} day streak` : 'No streak yet'}</span></div>
            <div class="bar"><i style="width:${pct}%"></i></div>
            <p class="sub">${p.nextLevelAt == null ? 'Top level. Nothing left to climb.' : `${fmt(p.toNext)} points to ${esc(p.nextLevelName || 'your next level')}.`}</p>
          </div>
          <div class="soc-herobadges">${badges.map((b) => `<span class="soc-hb" title="${esc(b.name || '')}">${badgeIcon(b)}</span>`).join('')}</div>
          <button class="btn soc-mini" id="socHeroGo">Compete</button>
        </div>
      </div>`;
    const go = $('socHeroGo');
    if (go) go.onclick = () => ctx.go('social');
  } catch (e) { console.warn('points strip failed', e.message); }
}
